import React, { useState, useEffect } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Row, Col } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useIsLotCnExists, usePaymentProjects } from "../hooks";

const LotCreateModal = ({ isOpen, toggle, onSubmit }) => {
    const [lotCn, setLotCn] = useState('');
    const [parsedLot, setParsedLot] = useState({
        project: '',
        phase: '',
        block: '',
        lot: ''
    });

    const {data: lotExistsData, isLoading: lotExistsLoading} = useIsLotCnExists(lotCn);
    const lotExists = lotExistsData?.exists || false;

    const lotCnUsable = !lotExists && lotCn.length === 12 && !lotExistsLoading;

    const [isSubmitting, setIsSubmitting] = useState(false);

    const {asDictionary: projectDict, isLoading: projectsIsLoading, asCnDictionary: projectCnDict} = usePaymentProjects();

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setLotCn('');
            setParsedLot({
                project: '',
                phase: '',
                block: '',
                lot: ''
            });
        }
    }, [isOpen]);

    const handleLotCnChange = (e) => {
        const value = e.target.value;
        setLotCn(value);

        // Parse lot_cn if it has the correct length
        if (value.length === 12) {
            const project = value.substring(0, 3);
            const phase = value.substring(3, 5);
            const block = value.substring(5, 8);
            const lot = value.substring(8, 12);

            setParsedLot({
                project,
                phase,
                block,
                lot,
                project_id: projectCnDict[project]?.id || 0 // Use projectDict to get the project ID
            });
        } else {
            setParsedLot({
                project: '',
                project_id: 0,
                phase: '',
                block: '',
                lot: ''
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (lotCn.length !== 12) {
            return;
        }

        setIsSubmitting(true);

        try {
            await onSubmit({
                lot_cn: lotCn,
                payment_project: parsedLot.project_id,
            });
            
            // Close the modal and reset form
            toggle();
        } catch (error) {
            console.error("Error adding lot:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            scrollable
        >
            <ModalHeader toggle={toggle} className="activegroup-header">
                Add New Lot
            </ModalHeader>
            <ModalBody className="px-5 py-4">
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label for="lotCn">Lot CN</Label>
                        <Input
                            type="text"
                            id="lotCn"
                            value={lotCn}
                            onChange={handleLotCnChange}
                            placeholder="Enter 12-character Lot CN"
                            maxLength={12}
                            required
                        />
                        <small className="text-muted">
                            Format: First 3 characters = Project CN, Next 2 = Phase, Next 3 = Block, Last 4 = Lot
                        </small>

                        {lotCn.length === 12 && (
                            <>
                                {lotExistsLoading && (
                                    <div className="mt-2">
                                        <small className="text-info">
                                            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                            Checking if lot CN is available...
                                        </small>
                                    </div>
                                )}

                                {!lotExistsLoading && lotExists && (
                                    <div className="mt-2">
                                        <small className="text-danger">
                                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                                            This lot CN already exists. Please use a different one.
                                        </small>
                                    </div>
                                )}

                                {!lotExistsLoading && !lotExists && (
                                    <div className="mt-2">
                                        <small className="text-success">
                                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                                            This lot CN is available and can be used.
                                        </small>
                                    </div>
                                )}
                            </>
                        )}
                    </FormGroup>

                    {lotCn.length === 12 && (
                        <div className="mt-4">
                            <h5>Parsed Information:</h5>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Project</Label>
                                        {projectCnDict[parsedLot.project] ? (
                                            <div>
                                                <Input
                                                    type="text"
                                                    value={`${projectCnDict[parsedLot.project].name} (${projectCnDict[parsedLot.project].ac_id})`}
                                                    disabled
                                                />
                                                {projectCnDict[parsedLot.project].description && (
                                                    <small className="text-muted d-block mt-1">
                                                        {projectCnDict[parsedLot.project].description}
                                                    </small>
                                                )}
                                            </div>
                                        ) : (
                                            <div>
                                                <Input
                                                    type="text"
                                                    value={`Unknown Project (${parsedLot.project})`}
                                                    disabled
                                                    className="text-danger"
                                                />
                                                <small className="text-danger d-block mt-1">
                                                    No project found with code: {parsedLot.project}
                                                </small>
                                            </div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Phase</Label>
                                        <Input
                                            type="text"
                                            value={parsedLot.phase}
                                            disabled
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Block</Label>
                                        <Input
                                            type="text"
                                            value={parsedLot.block}
                                            disabled
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Lot</Label>
                                        <Input
                                            type="text"
                                            value={parsedLot.lot}
                                            disabled
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleSubmit} disabled={isSubmitting || lotCn.length !== 12 || !lotCnUsable || lotExists || lotExistsLoading}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
                <Button color="secondary" onClick={toggle}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default LotCreateModal;